import { ObjectValues } from '@app/types';
import * as constants from '@redbubble/design-system/src/constants';
import Stack from '@redbubble/design-system/src/Stack';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, FormattedNumber, useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import React, { ReactNode } from 'react';
import styles from './Price.module.scss';
import { hasVat } from './utils';

export type Variant = 'defaultPriceStyle' | 'fromPriceStyle';
type Size = 'medium' | 'mediumSmall' | 'small' | 'smallCaption' | 'xsmall';
type Direction = 'vertical' | 'horizontal';
type TextAlign = 'left' | 'right';

export type VolumeDiscount = {
  thresholds: VolumeDiscountThreshold[];
};

export type VolumeDiscountThreshold = {
  percentOff: number;
  quantity: number;
};

type PromoDiscount = {
  amount: number;
  percent: number;
};

export interface Price {
  id: string;
  amount: number;
  currency: string;
  discount: PromoDiscount;
}

export interface PriceProps extends Pick<Price, 'amount' | 'currency'> {
  discount?: PromoDiscount;
  variant?: Variant;
  size?: Size;
  direction?: Direction;
  textAlign?: TextAlign;
  styleVariant?: PriceStyleVariant;

  // At the moment, we only show the VAT message for certain usages of the Price component
  hideVATOverride?: boolean;
}

const componentSize: {
  [size in Size]: {
    totalTextType: string;
    discountedTextType: string;
    vatTextType: string;
  };
} = {
  medium: {
    totalTextType: 'display2',
    discountedTextType: 'body2',
    vatTextType: 'body2',
  },
  mediumSmall: {
    totalTextType: 'display3',
    discountedTextType: 'body',
    vatTextType: 'body',
  },
  small: {
    totalTextType: 'display5',
    discountedTextType: 'body2',
    vatTextType: 'caption',
  },
  smallCaption: {
    totalTextType: 'display5',
    discountedTextType: 'caption',
    vatTextType: 'caption',
  },
  xsmall: {
    totalTextType: 'display6',
    discountedTextType: 'body2',
    vatTextType: 'caption',
  },
};

const componentDirection: {
  [dir in Direction]: { stackDirection: string; alignItem: string };
} = {
  horizontal: {
    stackDirection: constants.HORIZONTAL,
    alignItem: 'baseline',
  },
  vertical: {
    stackDirection: constants.VERTICAL,
    alignItem: 'start',
  },
};

const componentTextAlign: {
  [align in TextAlign]: string;
} = {
  left: constants.LEFT,
  right: constants.RIGHT,
};

const messages = defineMessages({
  percentOff: {
    defaultMessage: '{discount}% off',
  },
  inclVAT: {
    defaultMessage: 'incl. VAT',
  },
  fromPrice: {
    defaultMessage: 'From {price}',
  },
});

export const priceStyleVariant = {
  DEFAULT: 'default',
  SEARCH_RESULTS_PAGE_PRODUCT_CARD: 'searchResultsPageProductCard',
} as const;
export type PriceStyleVariant = ObjectValues<typeof priceStyleVariant>;

export const Vat = ({ size }: { size: Size }) => {
  const intl = useIntl();
  const [{ country }] = useUserInfo<UserInfo>();

  if (!hasVat(country)) return null;

  return (
    <Text type={componentSize[size].vatTextType} muted>
      {intl.formatMessage(messages.inclVAT)}
    </Text>
  );
};

interface PriceTextWrapperProps {
  size: Size;
  textAlign: TextAlign;
  children: ReactNode;
  hideVATOverride: boolean;
  className?: string;
}

export const PriceTextWrapper = ({
  size,
  textAlign,
  children,
  hideVATOverride,
  className,
}: PriceTextWrapperProps) => (
  <Stack direction={constants.HORIZONTAL} alignItems="baseline">
    <Text
      type={componentSize[size].totalTextType}
      className={className}
      marginRight="xxs"
      textAlign={componentTextAlign[textAlign]}
      data-testid="line-item-price-price"
    >
      {children}
    </Text>
    {!hideVATOverride && <Vat size={size} />}
  </Stack>
);

export const Price = ({
  amount,
  currency,
  discount,
  variant = 'defaultPriceStyle',
  size = 'medium',
  direction = 'vertical',
  textAlign = 'left',
  hideVATOverride = false,
  styleVariant = priceStyleVariant.DEFAULT,
}: PriceProps) => {
  const intl = useIntl();

  // Assumption: Amount is never $0.00
  const isDataMissing = !currency || !amount;
  if (isDataMissing) {
    return null;
  }

  const additionalStyles =
    styleVariant === priceStyleVariant.SEARCH_RESULTS_PAGE_PRODUCT_CARD
      ? styles.searchResultsPageProductCardPrice
      : '';

  if (variant === 'fromPriceStyle') {
    return (
      <PriceTextWrapper
        size={size}
        textAlign={textAlign}
        hideVATOverride={hideVATOverride}
        className={additionalStyles}
      >
        {intl.formatMessage(messages.fromPrice, {
          price: intl.formatNumber(amount, {
            currency,
            style: 'currency',
          }),
        })}
      </PriceTextWrapper>
    );
  }

  const showStrikethroughPricing = discount && discount?.percent > 0;

  const discountMessage = intl.formatMessage(messages.percentOff, {
    discount: discount?.percent,
  });

  return (
    <Stack
      direction={componentDirection[direction].stackDirection}
      alignItems={componentDirection[direction].alignItem}
    >
      <PriceTextWrapper
        size={size}
        textAlign={textAlign}
        hideVATOverride={hideVATOverride}
        className={
          showStrikethroughPricing
            ? [styles.discounted, additionalStyles].join(' ')
            : additionalStyles
        }
      >
        <FormattedNumber
          value={showStrikethroughPricing ? discount.amount : amount}
          style="currency"
          currency={currency}
        />
      </PriceTextWrapper>

      {showStrikethroughPricing && (
        <Text
          type={componentSize[size].discountedTextType}
          textAlign={componentTextAlign[textAlign]}
          muted
          data-testid="line-item-undiscounted-price"
        >
          <del>
            <FormattedNumber
              value={amount}
              style="currency"
              currency={currency}
            />
          </del>
          {` (${discountMessage})`}
        </Text>
      )}
    </Stack>
  );
};
